import type { ReactElement } from 'react';

import { TFunction } from '@mint-lib/i18n';

import { Box, Button, Icon, Tooltip, Typography } from '@myn/mui';

import { getTooltipMessage } from '../../constants/myCampaigns.js';
import { Co2Order, Co2OrderStatus } from '../../types/co2notifications.js';

type RenderButtonsStatusOptions = {
  status: Co2OrderStatus;
  item: Co2Order;
  onReject: () => void;
  onAccept: () => void;
  onWebsiteButtonClick: () => void;
  readOnlyUser?: boolean;
  co2ReadOnly: boolean;
  isCurrencyConverted?: boolean;
  isTopButtons?: boolean;
};

export const SERVER_DATE_FORMAT = 'yyyy-MM-dd' as const;
export const CLIENT_DATE_FORMAT = 'dd/MM/yyyy' as const;

export const useMapStatusToDescription = (
  t: TFunction,
): Record<Co2OrderStatus, string> => {
  return {
    [Co2OrderStatus.Booked]: t(
      "Your Buying Plan's carbon emissions were offset.",
    ),
    [Co2OrderStatus.Rejected]: t(
      "Your Buying Plan's carbon emissions were not offset.",
    ),
    [Co2OrderStatus.Draft]: t(
      'Your buying plan has come to an end, and you can now see the finalized cost to offset your carbon emissions. Confirm to offset your carbon emissions.',
    ),
    [Co2OrderStatus.Invoiced]: '',
  };
};
export const useMapStatusToTextMessage = (
  t: TFunction,
): Record<Co2OrderStatus, string> => ({
  [Co2OrderStatus.Booked]: t(
    'Service provided in collaboration with ClimatePartner',
  ),
  [Co2OrderStatus.Draft]: t(
    t('Service provided in collaboration with ClimatePartner'),
  ),
  [Co2OrderStatus.Invoiced]: '',
  [Co2OrderStatus.Rejected]: '',
});

export const mapStatusToButtons = (
  t: TFunction,
): Record<
  Co2OrderStatus,
  (options: RenderButtonsStatusOptions) => ReactElement
> => ({
  [Co2OrderStatus.Invoiced]: (options) => (
    <Tooltip title={t('Get the certificate from website')}>
      <Button variant="text" startIcon="Wikis">
        {t('Get certificate from website')}
      </Button>
    </Tooltip>
  ),
  [Co2OrderStatus.Booked]: (options) =>
    options.isTopButtons ? (
      <Box display="flex" gap={0.5} alignItems="center">
        <Icon
          sx={{ color: (theme) => theme.palette.success.dark }}
          component="CheckmarkOutline"
        />
        <Typography
          variant="bodyShort01"
          sx={{ color: (theme) => theme.palette.success.dark }}
        >
          {t('Accepted')}
        </Typography>
      </Box>
    ) : (
      <Tooltip title={t('Get the certificate from website')}>
        <Button
          startIcon="Wikis"
          variant="text"
          onClick={options.onWebsiteButtonClick}
        >
          {t('Get certificate from website')}
        </Button>
      </Tooltip>
    ),
  [Co2OrderStatus.Draft]: (options) => {
    const tooltipMassages = getTooltipMessage(t);
    const tooltip =
      options.readOnlyUser || options.co2ReadOnly
        ? tooltipMassages.CO2_NO_ACCESS
        : null;

    return (
      <Box display="flex" gap={1}>
        <Button
          onClick={options.onReject}
          size="small"
          startIcon="CloseOutline"
          color="error"
          disabled={
            options.readOnlyUser ||
            !options.isCurrencyConverted ||
            options.co2ReadOnly
          }
        >
          <Tooltip title={tooltip}>
            <span>{t('REJECT')}</span>
          </Tooltip>
        </Button>
        <Button
          onClick={options.onAccept}
          size="small"
          startIcon="CheckmarkOutline"
          disabled={
            options.readOnlyUser ||
            !options.isCurrencyConverted ||
            options.co2ReadOnly
          }
        >
          <Tooltip title={tooltip}>
            <span>{t('ACCEPT')}</span>
          </Tooltip>
        </Button>
      </Box>
    );
  },
  [Co2OrderStatus.Rejected]: () => (
    <Box display="flex" gap={0.5} alignItems="center">
      <Icon
        component="CloseOutline"
        sx={{ color: (theme) => theme.palette.error.dark }}
      />
      <Typography
        variant="bodyShort01"
        sx={{ color: (theme) => theme.palette.error.dark }}
      >
        {t('Rejected')}
      </Typography>
    </Box>
  ),
});
